import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LazyMotion, domAnimation } from 'framer-motion';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import UmamiAnalytics from './components/UmamiAnalytics';
import GoogleAnalytics from './components/GoogleAnalytics';
import { PILLAR_PAGES } from './data/pillarPages';
import { ThemeProvider } from './contexts/ThemeContext';

// Lazy-load des pages pour réduire le bundle initial
const HomePage = lazy(() => import('./pages/HomePage'));
const PricingPage = lazy(() => import('./pages/Pricing'));
const ExpertsPage = lazy(() => import('./pages/Experts'));
const TemplatesPage = lazy(() => import('./pages/Templates'));
const FreelancesPage = lazy(() => import('./pages/Freelances'));
const BlogPage = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const CategoriesPage = lazy(() => import('./pages/Categories'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const AdminNewsletter = lazy(() => import('./pages/AdminNewsletter'));
const AdminContacts = lazy(() => import('./pages/AdminContacts'));
const AdminTransactions = lazy(() => import('./pages/AdminTransactions'));
const Contact = lazy(() => import('./pages/Contact'));
const SitemapPage = lazy(() => import('./pages/Sitemap'));
const PromptGenerator = lazy(() => import('./pages/PromptGenerator'));
const GuideTemplatesNotion = lazy(() => import('./pages/GuideTemplatesNotion'));
const TemplatePage = lazy(() => import('./pages/TemplatePage'));
const PillarPage = lazy(() => import('./pages/PillarPage'));
const GeoPage = lazy(() => import('./pages/GeoPage'));
const FormationIA = lazy(() => import('./pages/FormationIA'));
const AgentsPage = lazy(() => import('./pages/Agents'));
const PartnersPage = lazy(() => import('./pages/Partners'));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <LazyMotion features={domAnimation}>
          <Router>
          <ScrollToTop />
          <UmamiAnalytics />
          <GoogleAnalytics />
          <div className="flex flex-col min-h-screen min-w-0 overflow-x-hidden">
            <Navbar />
            <main className="flex-grow">
              <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 dark:border-gray-600 dark:border-t-white rounded-full animate-spin" /></div>}>
                <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/tarification" element={<PricingPage />} />
                <Route path="/experts" element={<ExpertsPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/freelances" element={<FreelancesPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/page/:pageNum" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {PILLAR_PAGES.map((page) => (
                  <Route key={page.id} path={page.path} element={<PillarPage />} />
                ))}
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/partenaires" element={<PartnersPage />} />
                <Route path="/formation-ia-notion" element={<FormationIA />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/guide-templates-notion" element={<GuideTemplatesNotion />} />
                <Route path="/generateur-prompts" element={<PromptGenerator />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/templates-notion/:ville/:metier" element={<GeoPage />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route
                  path="/admin/products"
                  element={
                    <ProtectedRoute>
                      <AdminProducts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blog"
                  element={
                    <ProtectedRoute>
                      <AdminBlog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/newsletter"
                  element={
                    <ProtectedRoute>
                      <AdminNewsletter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/contacts"
                  element={
                    <ProtectedRoute>
                      <AdminContacts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/transactions"
                  element={
                    <ProtectedRoute>
                      <AdminTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route path="/:slug" element={<TemplatePage />} />
              </Routes>
              </Suspense>
            </main>
          </div>
        </Router>
        </LazyMotion>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
